// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { refineCurrencyAmountString } from '@/currency/api/types';
import { isoCurrencyCodeSchema } from '@/currency/constants/iso';
import { dateSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

/**
 * Form-state schema shared by the add and edit flows for a single charge on
 * a project billing plan. Numeric values are stored as nullable strings so the
 * user can freely type partial values; date picker values are stored as
 * DateTime/null and serialized when the charge row is saved.
 */
export const chargeFormSchema = z.object({
  label: z.string().trim().min(1, 'A label is required'),
  // Quantity is a nullable string so the input can actually be cleared.
  // Required/positive checks run at submit time via setError so the error
  // messages can reflect the pricing mode (quantity vs amount).
  quantity: z.string().nullable(),
  // The charge amount is a currency input object. The amount is a free-form
  // string (cleared to empty, validated at submit time); the isoCode is the
  // line item's single currency and is locked to it by the form field.
  unitPrice: z
    .object({
      amount: z.string(),
      isoCode: z.preprocess(
        (v) => (typeof v === 'string' ? v.toUpperCase() : v),
        isoCurrencyCodeSchema
      ),
    })
    .superRefine(refineCurrencyAmountString),
  servicePeriodStart: dateSchema.nullable(),
  servicePeriodEnd: dateSchema.nullable(),
  expectedBillableDate: dateSchema.nullable(),
});

export type ChargeFormData = z.infer<typeof chargeFormSchema>;
